import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #discoveryLoading = new Map();
  #discoveryEpoch = 0;
  #discoveryFetches = new Map();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    if (this.refs.grid?.dataset.discoveryGrid !== undefined && !this.refs.cards?.length && this.getAttribute('infinite-scroll') !== 'false') this.#renderNextPage();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#discoveryEpoch++;
    this.#discoveryFetches.clear();
    this.#discoveryLoading.clear();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try { await this.#fetchSpecificPage(page.page, page.url); }
    catch (error) { console.warn("Product page fetch failed.", error); }
    finally { resolvePromise(); }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const epoch = this.#discoveryEpoch;
    const key = epoch + ':' + pageInfo.url.toString();
    let pending = this.#discoveryFetches.get(key);
    if (!pending) {
      pending = sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
      this.#discoveryFetches.set(key, pending);
    }
    try {
      const pageContent = await pending;
      if (epoch === this.#discoveryEpoch && this.isConnected) this.pages.set(pageNumber, pageContent);
    } finally {
      if (this.#discoveryFetches.get(key) === pending) this.#discoveryFetches.delete(key);
    }
  }

  async #renderNextPage() {
    if (this.#discoveryLoading.has('next')) return;
    const epoch = this.#discoveryEpoch;
    this.#discoveryLoading.set('next', epoch);
    try {
      let empty = true;
      while (empty && this.isConnected && epoch === this.#discoveryEpoch) {
        const { grid } = this.refs;
        const nextPage = this.#getPage('next');
        if (!grid || !nextPage || !this.#shouldUsePage(nextPage)) break;
        if (!this.pages.has(nextPage.page)) await this.#fetchSpecificPage(nextPage.page, nextPage.url);
        const elements = this.#getGridForPage(nextPage.page);
        if (!elements || grid !== this.refs.grid || epoch !== this.#discoveryEpoch) break;
        grid.append(...elements);
        grid.dataset.lastLoadedPage = String(nextPage.page);
        this.#aspectRatioHelper?.processNewElements();
        empty = elements.length === 0;
        this.#syncDiscoveryFallback();
        if (!empty) {
          this.querySelector('[data-discovery-empty]')?.remove();
          history.replaceState('', '', nextPage.url.toString());
        }
        await yieldToMainThread();
      }
    } catch (error) {
      console.warn('Product pagination could not load the next page.', error);
    } finally {
      if (epoch === this.#discoveryEpoch) this.#discoveryLoading.delete('next');
    }
  }

  async #renderPreviousPage() {
    if (this.#discoveryLoading.has("previous")) return;
    const epoch = this.#discoveryEpoch;
    this.#discoveryLoading.set('previous', epoch);
    try {
    let empty = true;
    while (empty && this.isConnected && epoch === this.#discoveryEpoch) {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    if (!this.pages.has(previousPage.page)) await this.#fetchSpecificPage(previousPage.page, previousPage.url);
    const previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements || grid !== this.refs.grid || epoch !== this.#discoveryEpoch) return;

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);
    grid.dataset.firstLoadedPage = String(previousPage.page);
    empty = previousPageItemElements.length === 0;
    if (!empty) this.querySelector('[data-discovery-empty]')?.remove();

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
    }
    } catch (error) { console.warn('Product pagination could not load the previous page.', error); }
    finally { if (epoch === this.#discoveryEpoch) this.#discoveryLoading.delete('previous'); }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards, grid } = this.refs;
    const isPrevious = type === 'previous';
    const cursor = grid?.dataset[isPrevious ? 'firstLoadedPage' : 'lastLoadedPage'];
    const targetCard = cards?.[isPrevious ? 0 : cards.length - 1];
    const currentCardPage = Number(cursor || targetCard?.dataset.page || grid?.dataset.currentPage);
    if (!Number.isInteger(currentCardPage) || currentCardPage < 1) return;
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  #syncDiscoveryFallback() {
    const wrapper = this.querySelector('[data-discovery-pagination-fallback]');
    if (!wrapper) return;
    const next = this.#getPage('next');
    if (!next || !this.#shouldUsePage(next)) {
      wrapper.replaceChildren();
      return;
    }
    const link = document.createElement('a');
    link.href = next.url.toString();
    link.textContent = wrapper.querySelector('[rel="next"]')?.textContent?.trim() || 'Next';
    link.rel = 'next';
    wrapper.replaceChildren(link);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#discoveryEpoch++;
    this.#discoveryFetches.clear();
    this.#discoveryLoading.clear();
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
